// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// logSources (#247) — catalog + helpers for the in-app CloudWatch Log viewer.
//
// The viewer (formerly the dormant realm→TanStack query-comparison Logs screen)
// browses one kind of source: an AWS Lambda's CloudWatch log group, read through
// the queries Lambda's server-side `cwLogs` proxy so AWS credentials stay on the
// server.
//
// There is deliberately no env split. One Lambda serves BOTH uat and prod, so a
// group's logs are a single intermixed stream — offering a UAT/PROD toggle would
// imply a separation that does not exist.
//
// The source list is SERVER-OWNED — fetched from `cwLogGroups` — so growing from
// three Lambdas to all ~20-30 is a Lambda-side array edit with no client change.
//
// (An earlier draft of this screen also browsed FlooringXP's app telemetry from
// Convex. That moved into FlooringXP's own admin screen, where it belongs — this
// viewer is CloudWatch-only.)
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Safe nested accessor (dotted path). Local so the Log viewer never imports from
// Compass — the two are deliberately separate.
export function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  if (path in obj) return obj[path];
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

// CloudWatch column set (rows come from parseCwLogEvent).
export const CW_COLUMNS = [
  { label: "When", path: "__when" },
  { label: "Level", path: "level" },
  { label: "RequestId", path: "requestId" },
  { label: "Message", path: "text" },
];

// Build a source entry from a server `cwLogGroups` item ({id,label}).
//
// The label is used as-is (the server already says "Queries Lambda"): every
// source here is CloudWatch, so a "CloudWatch — " prefix on each button would be
// noise repeated across the row. The picker's own label says "Log group:".
//
// A malformed server entry yields null rather than a button reading "undefined"
// — the group list is server-owned, so a bad edit there must not render as a
// broken control the user can click.
export function cwSourceFromGroup(group) {
  if (!group || typeof group.id !== "string" || !group.id) return null;
  return {
    id: `cw:${group.id}`,
    label: group.label || group.id,
    cwGroupId: group.id,
    columns: CW_COLUMNS,
  };
}

// Time-window presets for the CloudWatch fetch.
export const TIME_WINDOWS = [
  { id: "15m", label: "Last 15 min", ms: 15 * 60 * 1000 },
  { id: "1h", label: "Last 1 hour", ms: 60 * 60 * 1000 },
  { id: "6h", label: "Last 6 hours", ms: 6 * 60 * 60 * 1000 },
  { id: "24h", label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  { id: "3d", label: "Last 3 days", ms: 3 * 24 * 60 * 60 * 1000 },
  { id: "7d", label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
];

export const DEFAULT_WINDOW_ID = "1h";

export function windowMs(id) {
  const w = TIME_WINDOWS.find((t) => t.id === id);
  return w ? w.ms : 60 * 60 * 1000;
}
